import React, { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Carousel, CarouselWrapper, CarouselArrowNavigation  } from '$partials/base/carousel';
import PropertyListingItem from '$partials/items/property-listing-item';
import { Button } from '$partials/ui';
import IconChevron from '$partials/icons/icon-chevron';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').PropertyCardCarousel}
 */

const PropertyCardCarousel = ({
    heading,
    items = [],
}) => {
    const [activeListingIndex, setActiveListingIndex] = useState(0);

    const [propertyItems, setPropertyItems] = useState(items?.[0]?.items || []);
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    const switchTab = async (idx) => {
        try {
            setIsLoadingItems(true);
            setPropertyItems([]);
            setActiveListingIndex(idx);
            const listing = items[idx];
            if (listing && listing?.url) {
                // const [url, qs] = listing.url.split('?');
                const listingResponse = await fetch(`/api/v1/listings${listing?.url}${listing?.url?.includes('?') ? '&_limit=12' : '?_limit=12'}`);
                if (listingResponse.ok) {
                    const result = await listingResponse.json();
                    setPropertyItems(result?.items?.map((edge) => edge.node));
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoadingItems(false);
        }
    };

    const swiperConfig = {
        slidesPerView: 'auto',
        spaceBetween: 0
    };

    const listingSwiperConfig = {
        slidesPerView: 'auto',
        spaceBetween: 0
    };

    return (
        <div className='module-property-card-carousel theme-light bg-main-background overflow-hidden'>
            <div className='py-16 sm:py-40 md:py-48'>
                <Carousel swiperConfig={swiperConfig}>
                    <div className='flex flex-col gap-16 sm:gap-24 w-full mx-auto'>
                        {/* Header with title and controls */}
                        <div className='layout-container layout-site-outer-padding flex flex-col sm:flex-row gap-24 sm:gap-8 items-start sm:items-baseline w-full'>
                            {/* Title */}
                            <div className='flex-1 w-full flex flex-col gap-24 items-start'>
                                {heading && (
                                    <h2 className='type-style-2 text-main-text-primary w-full'>
                                        {heading}
                                    </h2>
                                )}
                                {/* Desktop - listing tabs */}
                                <div className='hidden md:flex gap-x-6 overflow-x-auto'>
                                    {items?.length > 1 && (
                                        items?.map((listingGroup, index) => (
                                            <>
                                                <Button
                                                    className={`shrink-0 rounded type-style-eyebrow py-7 px-12 ${
                                                        activeListingIndex === index
                                                            ? 'bg-button-secondary-filled-background '
                                                            : 'button-secondary-transparent button-secondary-transparent--small'
                                                    }`}
                                                    key={listingGroup.listing?.id}
                                                    onClick={() => switchTab(index)}
                                                    type='button'>
                                                    {listingGroup.listing?.parent?.title}
                                                </Button>

                                            </>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Controls */}
                            <div className='hidden sm:flex items-center justify-end gap-16'>
                                <Button
                                    as='a'
                                    className='button-primary-transparent button-primary-transparent--small px-12 py-7 rounded flex gap-2 items-center justify-center type-style-eyebrow'
                                    href={items[activeListingIndex]?.url}>
                                    view all
                                    <IconChevron className='ml-10 -rotate-90' />
                                </Button>
                                <CarouselArrowNavigation />
                            </div>
                        </div>

                        {/* Mobile tablet - listing tabs */}
                        <div className='overflow-hidden block md:hidden w-full max-w-screen left-1/2 relative -translate-x-1/2'>
                            <div className='pl-16 sm:pl-48 overflow-visible'>
                                <Carousel swiperConfig={listingSwiperConfig}>
                                    <CarouselWrapper>
                                        {items?.length > 1 && (
                                            items?.map((listingGroup, index) => (
                                                <SwiperSlide
                                                    // className='w-10/12 sm:w-5/12 md:w-4/12 flex'
                                                    key={listingGroup?.listing?.id || index}>
                                                    <Button
                                                        className={`rounded type-style-eyebrow py-7 px-12 ${
                                                            activeListingIndex === index
                                                                ? 'bg-button-secondary-filled-background '
                                                                : 'button-secondary-transparent button-secondary-transparent--small'
                                                        }`}
                                                        key={listingGroup.listing?.id}
                                                        onClick={() => switchTab(index)}
                                                        type='button'>
                                                        {listingGroup.listing?.parent?.title}
                                                    </Button>
                                                </SwiperSlide>
                                            ))
                                        )}
                                    </CarouselWrapper>
                                </Carousel>
                            </div>
                        </div>
                        {/* Carousel */}
                        <div className='overflow-hidden w-full max-w-screen left-1/2 relative -translate-x-1/2'>
                            <div className='layout-container layout-site-outer-padding overflow-visible'>
                                <div className='col-wrapper'>
                                    {isLoadingItems && (
                                        <div className='aspect-[9/8] w-10/12 sm:w-5/12 md:w-4/12 flex'>
                                        </div>
                                    )}
                                    {!isLoadingItems && (
                                        <CarouselWrapper>
                                            {propertyItems?.map((property, index) => (
                                                <SwiperSlide
                                                    className='w-10/12 sm:w-5/12 md:w-4/12 flex'
                                                    key={property?.id || index}>
                                                    {property?.isOffMarket ? (
                                                        <div className='col-padding flex flex-1'>
                                                            <PropertyListingItem
                                                                isCarouselItem={true}
                                                                {...property} />
                                                        </div>
                                                    ) : (
                                                        <PropertyListingItem
                                                            isCarouselItem={true}
                                                            {...property} />
                                                    )}
                                                    {index < propertyItems.length - 1 && (
                                                        <div className='shrink-0 w-[1px] h-full bg-main-border'></div>
                                                    )}
                                                </SwiperSlide>
                                            ))}
                                        </CarouselWrapper>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </Carousel>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyCardCarousel;
